import React from 'react';
import styled from 'styled-components';

import { intelligenceScrollbar } from 'containers/Intelligence/theme/scrollbar';

import OrbitMessageList from '../../components/OrbitMessageList';
import OrbitTurnCard from '../../components/OrbitTurnCard';
import { ORBIT_SCROLL_CONTAINER_ATTR } from '../../threadScroll';
import { IOrbitPageDemoChat } from '../orbitPageChatSeed';
import OrbitPageComposer from './OrbitPageComposer';
import { pg, type } from './pageStyles';

/** Thread scrolls between the fixed top bar and the composer bar, which sits in the flex flow. */
const ChatArea = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
`;

// The first block starts 18 below the top bar (6265:4213 at y 100, under the 82 bar).
const Scroller = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  padding: 18px 24px 0;
  overflow-y: auto;
  ${intelligenceScrollbar('y')}
`;

// 6421:9962 draws the thread in the composer's 1068 column: replies at its left edge, questions
// flush with its right.
const Column = styled.div`
  width: 100%;
  max-width: 1068px;
  margin: 0 auto;
`;

// The last card ends 33.5 above the composer (6265:4255 at y 1033.47, 6421:10225 at 1067).
const Thread = styled.div`
  padding: 0 0 33.5px;

  > * + * {
    margin-top: 13.5px;
  }
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -8px 0 0 -8px;

  > * {
    margin: 8px 0 0 8px;
  }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  height: 29px;
  padding: 0 12px;
  border: 1px solid ${pg.border};
  border-radius: 999px;
  background: ${pg.rail};
  color: ${pg.secondary};
  white-space: nowrap;
  ${type('containerBody')}
`;

// No bar behind the composer: it sits on the page, in the thread's 1068 column, 33.4 above the
// frame's bottom edge (6421:10225 ends at y 1126.6 of 1160).
const ComposerBar = styled.div`
  flex: none;
  padding: 0 24px 33.4px;
`;

export const PageChatComposer: React.FC = () => (
  <ComposerBar data-testid="orbit-page-composer-bar">
    <Column>
      <OrbitPageComposer pickerPlacement="above" />
    </Column>
  </ComposerBar>
);

/**
 * The live conversation, from the provider, through the panel's own transcript renderer. The
 * scroller carries the panel body's marker, the only element the transcript scrolls.
 */
export const OrbitPageLiveChat: React.FC = () => (
  <ChatArea data-testid="orbit-page-live-chat">
    <Scroller data-testid="orbit-page-scroll" {...{ [ORBIT_SCROLL_CONTAINER_ATTR]: 'true' }}>
      <Column>
        <Thread>
          <OrbitMessageList variant="page" />
        </Thread>
      </Column>
    </Scroller>
    <PageChatComposer />
  </ChatArea>
);

/** A seeded demo conversation, rendered by the same turn card as a live one. */
export const OrbitPageDemoChat: React.FC<{ demo: IOrbitPageDemoChat }> = ({ demo }) => (
  <ChatArea data-testid="orbit-page-demo-chat">
    <Scroller data-testid="orbit-page-scroll">
      <Column>
        <Thread>
          <Chips aria-label="Context">
            {demo.chips.map(chip => (
              <Chip key={chip}>{chip}</Chip>
            ))}
          </Chips>
          {demo.turns.map(turn => (
            <OrbitTurnCard
              key={turn.id}
              question={turn.question}
              state={turn.state}
              turn={turn}
              variant="page"
            />
          ))}
        </Thread>
      </Column>
    </Scroller>
    <PageChatComposer />
  </ChatArea>
);
